// ---------------------------------------------------------------------------
// The best word and the best numbers round, for whoever the page is about.
//
// Shared by the account profile and an AI model's, for the same reason the
// distributions are: they are one question asked of one query, and two copies of
// this markup would eventually score the same nine differently.
//
// The score is recomputed here from the stored word rather than read back off
// the row. That is deliberate — it is the scoring rule applied to what was
// actually played, so a house rule that changes cannot leave a stale number on
// a profile page.
// ---------------------------------------------------------------------------

import { scoreLettersWord, scoreNumbersValue } from '@countdown/shared';

import { Card, CardTitle } from '../../components/primitives/Card.js';

export interface BestOfStats {
  bestWord: { word: string; length: number; at: number } | null;
  bestNumbersRound: { target: number; value: number; at: number } | null;
}

function shortDate(at: number): string {
  return new Date(at).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function BestOf({ stats }: { stats: BestOfStats }) {
  return (
    <Card>
      <CardTitle>Best of</CardTitle>
      <dl className="mt-3 flex flex-col gap-4">
        <div>
          <dt className="label-mono">best word</dt>
          {stats.bestWord ? (
            <dd className="mt-1.5 flex flex-wrap items-baseline gap-2">
              <span className="font-mono text-xl font-semibold tracking-[0.08em] uppercase">
                {stats.bestWord.word}
              </span>
              <span className="text-sm text-ink-muted">
                {stats.bestWord.length} letters for {scoreLettersWord(stats.bestWord.word, true)},{' '}
                {shortDate(stats.bestWord.at)}
              </span>
            </dd>
          ) : (
            <dd className="mt-1.5 text-sm text-ink-dim">No letters round scored yet.</dd>
          )}
        </div>
        <div>
          <dt className="label-mono">best numbers round</dt>
          {stats.bestNumbersRound ? (
            <dd className="mt-1.5 flex flex-wrap items-baseline gap-2">
              <span className="tabular font-mono text-xl font-semibold">
                {stats.bestNumbersRound.value}
              </span>
              <span className="text-sm text-ink-muted">
                target {stats.bestNumbersRound.target} for{' '}
                {scoreNumbersValue(stats.bestNumbersRound.value, stats.bestNumbersRound.target)},{' '}
                {shortDate(stats.bestNumbersRound.at)}
              </span>
            </dd>
          ) : (
            <dd className="mt-1.5 text-sm text-ink-dim">No numbers round scored yet.</dd>
          )}
        </div>
      </dl>
    </Card>
  );
}
